"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FaceRead = exports.FaceReadJsonKeys = exports.FaceHasTooManyErrorsException = void 0;
const undoverline_1 = require("./undoverline");
const drawing_1 = require("./drawing");
const bit_operations_1 = require("./bit-operations");
const face_dimensions_1 = require("./face-dimensions");
/**
 * Returns the majority value of a, b, c
 * If a != b != c, returns the first value
 * that isn't null or undefined
 */
const majorityOfThree = (a, b, c) => (a === b || a == c) ? a :
    (b === c) ? b :
        undefined;
class FaceHasTooManyErrorsException extends Error {
    constructor(faceRead) {
        super(`FaceRead can't be translated to face because errors make determination of letter or digit uncertain.`);
        this.faceRead = faceRead;
    }
}
exports.FaceHasTooManyErrorsException = FaceHasTooManyErrorsException;
exports.FaceReadJsonKeys = [
    "underline", "overline",
    "orientationAsLowercaseLetterTrbl",
    "ocrLetterCharsFromMostToLeastLikely", "ocrDigitCharsFromMostToLeastLikely",
    "center"
];
(() => {
    const testFaceReadJsonKeys = exports.FaceReadJsonKeys;
    return testFaceReadJsonKeys;
})();
class FaceRead {
    constructor(underline, overline, orientationAsLowercaseLetterTrbl, ocrLetterCharsFromMostToLeastLikely, ocrDigitCharsFromMostToLeastLikely, center) {
        var _a, _b, _c, _d;
        this.underline = underline;
        this.overline = overline;
        this.orientationAsLowercaseLetterTrbl = orientationAsLowercaseLetterTrbl;
        this.ocrLetterCharsFromMostToLeastLikely = ocrLetterCharsFromMostToLeastLikely;
        this.ocrDigitCharsFromMostToLeastLikely = ocrDigitCharsFromMostToLeastLikely;
        this.center = center;
        this.errors = [];
        this.toFace = () => {
            if (this.letter == null || this.digit == null || this.orientationAsLowercaseLetterTrbl == null) {
                throw new FaceHasTooManyErrorsException(this);
            }
            const { letter, digit, orientationAsLowercaseLetterTrbl } = this;
            return { letter, digit, orientationAsLowercaseLetterTrbl };
        };
        const ocrLetterRead = ocrLetterCharsFromMostToLeastLikely[0];
        const ocrDigitRead = ocrDigitCharsFromMostToLeastLikely[0];
        this.letter = majorityOfThree(ocrLetterRead, underline == null ? undefined : underline.letter, overline == null ? undefined : overline.letter);
        this.digit = majorityOfThree(ocrDigitRead, underline == null ? undefined : underline.digit, overline == null ? undefined : overline.digit);
        this.uniqueIdentifier =
            ocrLetterCharsFromMostToLeastLikely.substr(0, 2) +
                ocrDigitCharsFromMostToLeastLikely.substr(0, 2) + ':' +
                ((_b = (_a = underline === null || underline === void 0 ? void 0 : underline.code) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "no-underline-code") + ':' +
                ((_d = (_c = overline === null || overline === void 0 ? void 0 : overline.code) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : "no-overline-code") + ':' +
                center.x.toString() + ':' +
                center.y.toString();
        /////
        // Populate the errors field
        /////
        if (underline && overline &&
            underline.letter != null && underline.digit != null &&
            overline.letter != null && overline.digit != null &&
            underline.letter === overline.letter && underline.digit === overline.digit) {
            // The underline and overline map to the same face
            // Check for OCR errors for the letter read
            if (underline.letter != ocrLetterRead) {
                this.errors.push({
                    location: "letter",
                    type: underline.letter === ocrLetterCharsFromMostToLeastLikely[1] ?
                        "ocr-second-choice" : "ocr-mismatch"
                });
            }
            if (underline.digit != ocrDigitRead) {
                this.errors.push({
                    location: "digit",
                    type: underline.digit === ocrDigitCharsFromMostToLeastLikely[1] ?
                        "ocr-second-choice" : "ocr-mismatch"
                });
            }
        }
        else if (underline && underline.letter == ocrLetterRead && underline.digit == ocrDigitRead) {
            // The underline and the OCR-read letter & digit match, so the error is in the overline
            this.errors.push({
                location: "overline",
                ...(overline == null ? {
                    type: "undoverline-missing",
                } : {
                    type: "undoverline-bit-mismatch",
                    hammingDistance: (0, bit_operations_1.hammingDistance)(underline.faceWithUndoverlineCodes && underline.faceWithUndoverlineCodes.overlineCode || 0, overline.code)
                })
            });
            // All blocks below that could add errors are a result of else clauses, so
            // if we didn't find an OCR error, we're done without finding any errors.
        }
        else if (overline && overline.letter == ocrLetterRead && overline.digit == ocrDigitRead) {
            // The overline and the OCR-read letter and digit match, any errors are in the underline
            this.errors.push({
                location: "underline",
                ...(overline == null ? {
                    type: "undoverline-missing",
                } : {
                    type: "undoverline-bit-mismatch",
                    hammingDistance: (0, bit_operations_1.hammingDistance)(overline.faceWithUndoverlineCodes && overline.faceWithUndoverlineCodes.underlineCode || 0, overline.code)
                })
            });
        }
        else if (underline == null && overline == null) {
            // If we've made it this far down the if/then/else block, it's possible
            // that neither and underline or overline was read
            this.errors.push({
                type: "no-undoverline-or-overline-with-which-to-locate-face"
            });
        }
        else {
            // At least an overline OR an underline was read, but we still don't have
            // majority agreement on either a letter, a digit, or both
            this.errors.push({
                type: "no-majority-agreement"
            });
        }
    }
    get undoverlines() {
        return [this.underline, this.overline]
            .filter(u => u != null);
    }
    get inferredAngleInRadians() {
        const NinetyDegreesAsRadians = Math.PI / 2;
        const undoverlines = this.undoverlines;
        const angleInRadians = (undoverlines.length === 2) ?
            ((0, drawing_1.angleOfLineInSignedRadians2f)({
                start: (0, drawing_1.midpointOfLine)(undoverlines[0].line), end: (0, drawing_1.midpointOfLine)(undoverlines[1].line)
            }) +
                NinetyDegreesAsRadians) :
            (undoverlines.length === 1) ?
                (0, drawing_1.angleOfLineInSignedRadians2f)(undoverlines[0].line) :
                0;
        if (angleInRadians > Math.PI) {
            return angleInRadians - 2 * Math.PI;
        }
        else {
            return angleInRadians;
        }
    }
    get inferredUndoverlineLength() {
        const undoverlines = this.undoverlines;
        return (undoverlines.length === 2) ?
            (((0, drawing_1.lineLength)(undoverlines[0].line) + (0, drawing_1.lineLength)(undoverlines[1].line)) / 2) :
            (undoverlines.length === 1) ?
                (0, drawing_1.lineLength)(undoverlines[0].line) :
                0;
    }
    get inferredFaceSize() {
        return this.inferredUndoverlineLength / face_dimensions_1.FaceDimensionsFractional.undoverlineLength;
    }
}
exports.FaceRead = FaceRead;
FaceRead.fromJsonObject = (j) => new FaceRead(undoverline_1.Undoverline.fromJsonUnderlineObject(j.underline), undoverline_1.Undoverline.fromJsonOverlineObject(j.overline), j.orientationAsLowercaseLetterTrbl, j.ocrLetterCharsFromMostToLeastLikely, j.ocrDigitCharsFromMostToLeastLikely, j.center);
FaceRead.fromJson = (facesReadJson) => {
    if (facesReadJson.length == 0) {
        return undefined;
    }
    const facesReadObj = JSON.parse(facesReadJson);
    if (facesReadObj == null || !Array.isArray(facesReadObj) || facesReadObj.length !== 25) {
        return undefined;
    }
    return facesReadObj.map(FaceRead.fromJsonObject);
};
//# sourceMappingURL=face-read.js.map